import { useState, useEffect, useCallback, useRef } from 'react';
import { queueAPI } from '../../../api/queue';
import { appointmentAPI } from '../../../api/appointment';
import { useQueueSocket } from '../../../hooks/useQueueSocket';
import crossTab from '../../../utils/crossTabCommunication';

export const usePatientQueue = (userId) => {
  const [patientQueue, setPatientQueue] = useState([]);
  const [queueStats, setQueueStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef(0);
  const FETCH_COOLDOWN = 2000; // Minimum 2 seconds between fetches to avoid 429

  // Real-time queue updates via Socket.IO
  const {
    isConnected: socketConnected,
    isSubscribed: socketSubscribed,
    queueData: realtimeQueueData,
    summary: realtimeSummary,
    currentPatient: realtimeCurrentPatient,
    nextPatient: realtimeNextPatient,
  } = useQueueSocket(userId, {
    showNotifications: false, // Disable socket notifications to prevent duplicate toasts
    onQueueUpdated: (data) => {
      if (data.data) {
        updateQueueFromRealtimeData(data.data);
      }
    },
    onPatientAdded: (data) => {
      fetchQueue(true);
    },
    onPatientRemoved: (data) => {
      fetchQueue(true);
    },
    onServiceStarted: (data) => {
      fetchQueue(true);
    },
    onServiceCompleted: (data) => {
      fetchQueue(true);
    },
    onAppointmentBooked: (data) => {
      fetchQueue(true);
    },
  });

  // Calculate waiting time from queueJoinedAt timestamp
  const calculateWaitingTime = (queueJoinedAt) => {
    if (!queueJoinedAt) return 'N/A';
    
    const joinedTime = new Date(queueJoinedAt);
    const currentTime = new Date();
    const diffInMinutes = Math.floor((currentTime - joinedTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just joined';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  // Update queue from real-time socket data
  const updateQueueFromRealtimeData = useCallback((data) => {
    const queueEntries = (data.queueEntries || []).map(entry => ({
      ...entry,
      waitingTime: calculateWaitingTime(entry.queueJoinedAt),
      estimatedWaitTime: entry.estimatedWaitTime || 0,
      patient: {
        ...entry.patient,
        photo: entry.patient?.photo ? {
          url: entry.patient.photo.url || entry.patient.photo,
          ...entry.patient.photo
        } : null
      }
    }));
    
    const queueSummary = {
      totalInQueue: data.summary?.inQueue || queueEntries.length,
      currentlyServing: !!data.currentPatient,
      currentPatient: data.currentPatient,
      nextPatient: data.nextPatient,
      estimatedTotalTime: (data.summary?.inQueue || 0) * 30,
      lastUpdated: data.date || new Date(),
      summary: data.summary
    };
    
    setPatientQueue(queueEntries);
    setQueueStats(queueSummary);
  }, []);

  // Fetch doctor's queue using new API with fallback
  const fetchQueue = useCallback(async (force = false) => {
    if (!userId) {
      return;
    }

    // Rate limiting: prevent too many requests
    const now = Date.now();
    if (!force && (now - lastFetchRef.current) < FETCH_COOLDOWN) {
      return;
    }
    lastFetchRef.current = now;
    
    try {
      setLoading(true);
      
      // Try new queue API endpoint first: /queue/{doctorId}
      try {
        const response = await queueAPI.getDoctorQueue(userId);
        
        if (response.success) {
          const apiData = response.data;
          
          // Map queue entries with waiting time calculation
          const queueEntries = (apiData.queueEntries || []).map(entry => ({
            ...entry,
            waitingTime: calculateWaitingTime(entry.queueJoinedAt),
            estimatedWaitTime: entry.estimatedWaitTime || 0,
            patient: {
              ...entry.patient,
              // Ensure photo URL is properly structured
              photo: entry.patient?.photo ? {
                url: entry.patient.photo.url || entry.patient.photo,
                ...entry.patient.photo
              } : null
            }
          }));
          
          const queueSummary = {
            totalInQueue: apiData.queueSummary?.totalInQueue || queueEntries.length,
            currentlyServing: !!apiData.queueSummary?.currentPatient,
            currentPatient: apiData.queueSummary?.currentPatient,
            nextPatient: apiData.queueSummary?.nextPatient,
            estimatedTotalTime: (apiData.queueSummary?.totalInQueue || 0) * 30,
            lastUpdated: apiData.queueSummary?.lastUpdated || new Date(),
            summary: apiData.queueSummary
          };
          
          setPatientQueue(queueEntries);
          setQueueStats(queueSummary);
          return;
        }
      } catch (queueError) {
        // Fallback to summary endpoint
        try {
          const response = await queueAPI.getQueueSummary(userId);
          
          if (response.success) {
            // Handle the actual API response structure
            const apiData = response.data;
            
            // Use the queueEntries if available, otherwise construct from summary
            const queueEntries = (apiData.queueEntries || []).map(entry => ({
              ...entry,
              waitingTime: calculateWaitingTime(entry.queueJoinedAt),
              estimatedWaitTime: entry.estimatedWaitTime || 0,
              patient: {
                ...entry.patient,
                // Ensure photo URL is properly structured
                photo: entry.patient?.photo ? {
                  url: entry.patient.photo.url || entry.patient.photo,
                  ...entry.patient.photo
                } : null
              }
            }));
            
            if (queueEntries.length === 0 && apiData.nextPatient) {
              // Fallback: if no queueEntries but we have nextPatient, create a basic entry
              queueEntries.push({
                _id: apiData.nextPatient.appointmentId,
                patient: {
                  firstName: apiData.nextPatient.patientName?.split(' ')[0] || '',
                  lastName: apiData.nextPatient.patientName?.split(' ').slice(1).join(' ') || '',
                  mobile: apiData.nextPatient.mobile || '',
                  email: apiData.nextPatient.email || ''
                },
                queuePosition: apiData.nextPatient.queuePosition,
                status: 'IN_QUEUE',
                estimatedWaitTime: apiData.nextPatient.estimatedWaitTime,
                waitingTime: 'N/A'
              });
            }
            
            const queueSummary = {
              totalInQueue: apiData.summary?.inQueue || 0,
              currentlyServing: !!apiData.currentPatient,
              currentPatient: apiData.currentPatient,
              nextPatient: apiData.nextPatient,
              estimatedTotalTime: (apiData.summary?.inQueue || 0) * 30,
              lastUpdated: apiData.date,
              summary: apiData.summary
            };
            
            setPatientQueue(queueEntries);
            setQueueStats(queueSummary);
            return;
          }
        } catch (summaryError) {
          // Final fallback to legacy appointment API methods
          try {
            const [queueResponse, statsResponse] = await Promise.all([
              appointmentAPI.getDoctorQueue(userId),
              appointmentAPI.getQueueSummary(userId)
            ]);
            
            if (queueResponse.success) {
              const queueWithWaitTime = (queueResponse.data.queue || []).map(entry => ({
                ...entry,
                waitingTime: calculateWaitingTime(entry.queueJoinedAt || entry.joinedAt),
                estimatedWaitTime: entry.estimatedWaitTime || 0,
                patient: {
                  ...entry.patient,
                  // Ensure photo URL is properly structured
                  photo: entry.patient?.photo ? {
                    url: entry.patient.photo.url || entry.patient.photo,
                    ...entry.patient.photo
                  } : null
                }
              }));
              setPatientQueue(queueWithWaitTime);
            }
            
            if (statsResponse.success) {
              setQueueStats(statsResponse.data);
            }
            return;
          } catch (fallbackError) {
            // Silently fail
          }
        }
      }
      
      // If all else fails, clear the data
      setPatientQueue([]);
      setQueueStats(null);
      
    } catch (error) {
      setPatientQueue([]);
      setQueueStats(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Start service for selected patient
  const handleStartService = async (appointmentId) => {
    try {
      setLoading(true);
      const response = await queueAPI.startService(appointmentId);
      if (response.success) {
        await fetchQueue(); // Refresh queue
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error starting service:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Complete service for selected patient
  const handleCompleteService = async (appointmentId) => {
    try {
      setLoading(true);
      const response = await queueAPI.completeService(appointmentId);
      if (response.success) {
        await fetchQueue(); // Refresh queue
        return true; // Indicate success for calling component
      }
      return false;
    } catch (error) {
      console.error('Error completing service:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Join appointment to queue
  const handleJoinQueue = async (appointmentId) => {
    try {
      setLoading(true);
      const response = await queueAPI.joinQueue(appointmentId);
      if (response.success) {
        await fetchQueue(); // Refresh queue
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error joining queue:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Mark patient as arrived
  const handleMarkArrived = async (appointmentId) => {
    try {
      setLoading(true);
      const response = await queueAPI.markArrived(appointmentId);
      if (response.success) {
        await fetchQueue(); // Refresh queue
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error marking patient as arrived:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Auto-join eligible appointments
  const handleAutoJoin = async () => {
    try {
      setLoading(true);
      const response = await queueAPI.autoJoinAppointments(userId);
      if (response.success) {
        await fetchQueue(); // Refresh queue
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error auto-joining appointments:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Load queue on component mount
  useEffect(() => {
    fetchQueue(true); // Force initial fetch
  }, [fetchQueue]);

  // Update from real-time data when available
  useEffect(() => {
    if (realtimeQueueData && socketSubscribed) {
      updateQueueFromRealtimeData(realtimeQueueData);
    }
  }, [realtimeQueueData, socketSubscribed, updateQueueFromRealtimeData]);

  // Listen for cross-tab notifications (when Socket.IO is not available)
  useEffect(() => {
    const handleAppointmentBooked = (data) => {
      if (data.doctorId === userId) {
        fetchQueue(true);
      }
    };

    const handleQueueRefresh = (data) => {
      if (data.doctorId === userId) {
        fetchQueue(true);
      }
    };

    crossTab.onAppointmentBooked(handleAppointmentBooked);
    crossTab.onQueueRefresh(handleQueueRefresh);

    return () => {
      crossTab.off('hms:appointment:booked', handleAppointmentBooked);
      crossTab.off('hms:queue:refresh', handleQueueRefresh);
    };
  }, [userId, fetchQueue]);

  return {
    patientQueue,
    queueStats,
    loading,
    fetchQueue,
    handleStartService,
    handleCompleteService,
    handleJoinQueue,
    handleMarkArrived,
    handleAutoJoin,
    calculateWaitingTime, // Export the utility function
    refreshQueue: fetchQueue,
    // Socket connection status
    socketConnected,
    socketSubscribed,
  };
};
